import Cookies from 'js-cookie';
import { computed, makeObservable, runInAction } from 'mobx';
import { RootStore } from 'core/stores/root-store';
import { AsyncState } from 'shared/libs/state';
import { HttpClient } from 'shared/libs/http';
import { ApiError } from 'shared/types';
import { UserApiService } from 'user';
import { User } from 'user/types';
import { RAuthResponse } from './r-auth.types';

const MOCK_R_AUTH_TOKEN = 'mock-success';
const mockRAuthResponse: RAuthResponse = {
   response: {
      user: {
         user_id: 145450860,
         employer_id: 0,
         first_name: 'Маріанна',
         last_name: 'Науменко',
         company_name: null,
         user_type: 'candidate',
         email_address: 'drew.naumenko@gmail.com',
         profile_image: null,
         contact: '+380932808421',
         address: null,
         role: null,
         city_id: null,
         birthday: null,
      },
      token:
         'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2dyYy5uZXQudWEvYXBpL3VzZXJzL2xvZ2luIiwiaWF0IjoxNzg2NTUwOTExLCJleHAiOjE3ODkxNDI5MTEsIm5iZiI6MTc4NjU1MDkxMSwianRpIjoicjlBVUNCRG5LRmpOYXl0ZSIsInN1YiI6IjE0NTQ1MDg2MCIsInBydiI6IjIzYmQ1Yzg5NDlmNjAwYWRiMzllNzAxYzQwMDg3MmRiN2E1OTc2ZjcifQ.IC4_AEwNyRMJiohk4LOkahQA0GqUcRqZO94cDiABygg',
   },
   message: 'Logged in via SMS link',
   status: 200,
};

type RAuthResult =
   | {
        success: true;
     }
   | {
        success: false;
        status?: number;
        message?: string;
     };

export class RAuthStore {
   readonly authState = new AsyncState<RAuthResponse>();

   constructor(
      readonly rootStore: RootStore,
      private readonly language: string
   ) {
      makeObservable(this, {
         authorizing: computed,
      });
   }

   get authorizing(): boolean {
      return this.authState.status === 'pending';
   }

   private setUserCookies(user: User): void {
      Cookies.set('user_id', String(user.user_id));
      Cookies.set('tokenClient', user.token);
      Cookies.set('user_type', user.user_type);

      if (user.user_type === 'recruiter') {
         Cookies.set('employer_role', user.role ?? '');
         Cookies.set('fname', user.company_name ?? '');
         Cookies.set('employer_id', String(user.employer_id));
         return;
      }

      Cookies.set('lname', user.last_name);
      Cookies.set('fname', user.first_name);
      Cookies.set('contactNumber', user.contact || '');
      Cookies.set('emailAddress', user.email_address);
   }

   private getToken(response: RAuthResponse): string {
      return response.response?.user?.token || response.response?.token || '';
   }

   private isSuccessResponse(response: RAuthResponse): boolean {
      return response.status === 200 && Boolean(response.response?.user) && Boolean(this.getToken(response));
   }

   private getAuthorizedUser(response: RAuthResponse): User {
      return {
         ...response.response.user,
         token: this.getToken(response),
      };
   }

   private getErrorStatus(error: ApiError): number | undefined {
      const descCode = error.details?.desc?.code;

      if (typeof descCode === 'number') {
         return descCode;
      }

      return error.status;
   }

   private async loadAuthResponse(token: string): Promise<RAuthResponse> {
      if (token === MOCK_R_AUTH_TOKEN) {
         return mockRAuthResponse;
      }

      return HttpClient.get<RAuthResponse>(`/r/${encodeURIComponent(token)}`);
   }

   async authorize(
      token: string,
      {
         onSuccess,
         onError,
      }: {
         onSuccess?: (response: RAuthResponse) => void | Promise<void>;
         onError?: (message: string, status?: number) => void | Promise<void>;
      } = {}
   ): Promise<RAuthResult> {
      let result: RAuthResult = { success: false };

      await this.authState.run(
         () => this.loadAuthResponse(token),
         {
            onSuccess: async (response) => {
               if (!this.isSuccessResponse(response)) {
                  result = { success: false, status: response.status, message: response.message };
                  await onError?.(response.message, response.status);
                  return;
               }

               const user = this.getAuthorizedUser(response);

               if (token !== MOCK_R_AUTH_TOKEN) {
                  await UserApiService.setContext({
                     user_id: user.user_id,
                     user_type: user.user_type,
                     lang: this.language,
                  });
               }

               runInAction(() => {
                  this.rootStore.userStore.setUser(user);
               });

               window.dataLayer = window.dataLayer || [];
               window.dataLayer.push({
                  event: 'login',
                  user_id: user.user_id,
                  authentication_method: 'redirect',
               });

               this.setUserCookies(user);
               result = { success: true };
               await onSuccess?.(response);
            },
            onError: async (error) => {
               const status = this.getErrorStatus(error);

               result = { success: false, status, message: error.message };
               await onError?.(error.message, status);
            },
         }
      );

      return result;
   }
}
